class DropDown extends HTMLElement {
  constructor() {
    super();
    this.dropDownFunction = this.dropDownFunction.bind(this);
    this.sameToggle = this.getAttribute('sameToggle');
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleOptionClick = this.handleOptionClick.bind(this);
    this.toggleDropdown = this.toggleDropdown.bind(this);
    this.currentOptionIndex = 0;
  }

  dropDownFunction() {
    this.dropdown = this.querySelector('.dropdown');
    this.dropdownButton = this.querySelector('.dropdown-button');
    this.dropdownMenu = this.querySelector('.dropdown-menu');

    if (this.dropdown && this.dropdownButton && this.dropdownMenu) {
      this.dropdownButton.addEventListener('click', this.toggleDropdown);
      this.dropdownMenu.addEventListener('click', this.handleOptionClick);
      document.addEventListener('click', this.handleClickOutside);
    }
  }

  getFloatingLabel() {
    const labelId = this.getAttribute('data-label');
    return labelId ? document.getElementById(labelId) : null;
  }

  toggleDropdown() {
    const sameDropDowns = document.querySelectorAll(`[sameToggle=${this.sameToggle}]`);
    const isOpen = this.dropdown.classList.toggle('open');
    this.dropdown.setAttribute('data-open', isOpen);

    if (sameDropDowns.length) {
      sameDropDowns.forEach((dropDown) => {
        if (dropDown.sameToggle === this.sameToggle && dropDown !== this) {
          dropDown.dropdown.classList.remove('open');
        }
      });
    }
    const label = this.getFloatingLabel();
    if (isOpen) {
      this.dropdownButton?.setAttribute('aria-expanded', 'true');
      label?.classList.add('subject__field-open');
      this.enableKeyboardNavigation();
    } else {
      this.dropdownButton?.setAttribute('aria-expanded', 'false');
      label?.classList.remove('subject__field-open');
      this.disableKeyboardNavigation();
    }
  }

  closeDropdown() {
    this.dropdown.classList.remove('open');
    this.dropdown.setAttribute('data-open', false);
    this.dropdownButton?.setAttribute('aria-expanded', 'false');
    const label = this.getFloatingLabel();
    label?.classList.remove('subject__field-open');
    this.disableKeyboardNavigation();
  }

  enableKeyboardNavigation() {
    this.options = Array.from(this.dropdownMenu.querySelectorAll('[role="option"]'));
    if (!this.options.length) return;

    this.currentOptionIndex = 0;
    this.options[0].setAttribute('tabindex', '0');
    this.options[0].focus();

    document.addEventListener('keydown', this.handleKeydown);
  }

  disableKeyboardNavigation() {
    document.removeEventListener('keydown', this.handleKeydown);
  }

  handleOptionClick(event) {
    const option = event.target.closest('[role="option"]');
    if (option && this.dropdownMenu?.contains(option)) {
      this.selectOption(option);
    }
  }

  handleKeydown(e) {
    if (!this.dropdown.classList.contains('open') || !this.options?.length) return;

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      this.options[this.currentOptionIndex].setAttribute('tabindex', '-1');

      if (e.key === 'ArrowDown') {
        this.currentOptionIndex = (this.currentOptionIndex + 1) % this.options.length;
      } else {
        this.currentOptionIndex = (this.currentOptionIndex - 1 + this.options.length) % this.options.length;
      }

      this.options[this.currentOptionIndex].setAttribute('tabindex', '0');
      this.options[this.currentOptionIndex].focus();
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      this.selectOption(this.options[this.currentOptionIndex]);
    }

    if (e.key === 'Escape') {
      this.closeDropdown();
      this.dropdownButton.focus();
    }
  }

  selectOption(option) {
    this.dropdownMenu.querySelectorAll('[role="option"]').forEach((opt) => {
      opt.setAttribute('aria-selected', 'false');
    });
    option.setAttribute('aria-selected', 'true');

    const selectedText = option.textContent.trim();
    const labelSpan = this.dropdownButton.querySelector('.dropdown-selected-text');
    if (labelSpan) {
      labelSpan.textContent = selectedText;
    }
    this.dropdownButton.classList.remove('inactive');

    const inputId = this.getAttribute('data-input');
    if (inputId) {
      const hiddenInput = document.getElementById(inputId);
      if (hiddenInput) {
        hiddenInput.value = selectedText;
        hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    this.closeDropdown();
    const label = this.getFloatingLabel();
    label?.classList.add('subject__field-selected');
  }

  handleClickOutside(event) {
    if (!this.contains(event.target) && this.dropdown.classList.contains('open')) {
      this.closeDropdown();
    }
  }

  syncFromHiddenInput() {
    const inputId = this.getAttribute('data-input');
    const hiddenInput = inputId && document.getElementById(inputId);
    if (!hiddenInput?.value) return;

    const match = Array.from(this.dropdownMenu.querySelectorAll('[role="option"]')).find(
      (option) => option.textContent.trim() === hiddenInput.value
    );
    if (!match) return;

    const labelSpan = this.dropdownButton?.querySelector('.dropdown-selected-text');
    if (labelSpan) {
      labelSpan.textContent = hiddenInput.value;
    }
    this.dropdownButton?.classList.remove('inactive');
    match.setAttribute('aria-selected', 'true');
    this.getFloatingLabel()?.classList.add('subject__field-selected');
  }

  connectedCallback() {
    this.dropDownFunction();
    this.syncFromHiddenInput();
  }

  disconnectedCallback() {
    if (this.dropdownButton) {
      this.dropdownButton.removeEventListener('click', this.toggleDropdown);
    }
    if (this.dropdownMenu) {
      this.dropdownMenu.removeEventListener('click', this.handleOptionClick);
    }
    document.removeEventListener('click', this.handleClickOutside);
    this.disableKeyboardNavigation();
  }
}

const init = () => {
  if (!customElements.get('custom-dropdown')) {
    customElements.define('custom-dropdown', DropDown);
  }
};

init();

export default DropDown;
